import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/client';
import { useAdminCampaigns } from '../../hooks/useAdminCampaigns';
import CSRWorkloadCard from './CSRWorkloadCard';
import CampaignFilters, { FilterState } from './CampaignFilters';
import BulkAssignmentPanel from './BulkAssignmentPanel';
import EnhancedStatusBadge from './EnhancedStatusBadge';
import { 
  filterCampaignsByAssignment, 
  filterCampaignsByStatus,
  searchCampaigns,
  calculateWorkloadStats,
  getAssignmentType,
  getAssignmentTypeStyles,
  getAssignmentTypeLabel,
  DEFAULT_WORKLOAD_THRESHOLD
} from '../../utils/csrAssignment';


const EnhancedCSRAssignment: React.FC = () => {
  const { campaigns, csrWorkloads, loading, error: hookError, refetch, refreshWorkloads } = useAdminCampaigns();
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'assigned' | 'unassigned'>('all');
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);
  const [bulkAssignCSR, setBulkAssignCSR] = useState<string>('');
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [selectedCSR, setSelectedCSR] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    status: 'all',
    assignment: 'all',
    dateRange: { from: '', to: '' }
  });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [companiesData, setCompaniesData] = useState<{ [key: string]: string }>({});
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [campaignSortConfig, setCampaignSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' });
  const [workloadThreshold, setWorkloadThreshold] = useState<number>(DEFAULT_WORKLOAD_THRESHOLD);
  const [showThresholdSettings, setShowThresholdSettings] = useState(false);

  const displayError = error || (hookError?.message);
  
  // Fetch company CSR assignments for assignment type detection
  const fetchCompanyData = useCallback(async () => {
    try {
      const { data: companies, error: companyError } = await supabase
        .from('companies')
        .select('id, assigned_csr_id')
        .not('assigned_csr_id', 'is', null);
      
      if (!companyError && companies) {
        const companyMap = companies.reduce((acc, company) => {
          acc[company.id] = company.assigned_csr_id;
          return acc;
        }, {} as { [key: string]: string });
        setCompaniesData(companyMap);
      }
    } catch (err) {
      console.error('Failed to fetch company data:', err);
    }
  }, []);
  
  // Fetch company data on component mount and when campaigns change
  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData, campaigns]);
  
  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      refreshWorkloads();
      setLastRefresh(new Date());
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, [autoRefresh, refreshWorkloads]);
  
  // Calculate workload statistics for display
  const workloadStats = calculateWorkloadStats(csrWorkloads);
  
  // Create CSR users array from workloads for compatibility
  const csrUsers = csrWorkloads.map(workload => ({
    id: workload.csr_id,
    email: workload.csr_email
  }));

  // Handle individual CSR assignment
  const handleAssignCSR = async (campaignId: string, csrId: string | null) => {
    try {
      const { error } = await supabase
        .from('campaigns')
        .update({ assigned_csr: csrId })
        .eq('id', campaignId);

      if (error) {
        setError(`Error assigning CSR: ${error.message}`);
        return;
      }

      refetch();
      refreshWorkloads(); // Refresh workload data after assignment
      setError('');
    } catch (err) {
      setError('Failed to assign CSR');
      console.error('Error assigning CSR:', err);
    }
  };

  // Handle bulk CSR assignment
  const handleBulkAssign = async () => {
    if (selectedCampaigns.length === 0 || !bulkAssignCSR) {
      setError('Please select campaigns and a CSR for bulk assignment');
      return;
    }

    setBulkProcessing(true);
    try {
      const { error } = await supabase
        .from('campaigns')
        .update({ assigned_csr: bulkAssignCSR })
        .in('id', selectedCampaigns);

      if (error) {
        setError(`Error in bulk assignment: ${error.message}`);
        return;
      }

      setSelectedCampaigns([]);
      setBulkAssignCSR('');
      setShowBulkActions(false);
      refetch();
      refreshWorkloads(); // Refresh workload data after assignment
      setError('');
    } catch (err) {
      setError('Failed to perform bulk assignment');
      console.error('Error in bulk assignment:', err);
    } finally {
      setBulkProcessing(false);
    }
  };

  // Auto-assign based on workload
  const handleAutoAssign = async () => {
    const unassignedCampaigns = campaigns.filter(campaign => !campaign.assigned_csr);
    
    if (unassignedCampaigns.length === 0) {
      setError('No unassigned campaigns to auto-assign');
      return;
    }

    try {
      // Sort CSRs by workload (ascending - least busy first)
      const sortedCSRs = [...csrWorkloads].sort((a, b) => a.total_workload_score - b.total_workload_score);
      
      if (sortedCSRs.length === 0) {
        setError('No CSRs available for auto-assignment');
        return;
      }

      // Assign campaigns to CSRs in round-robin fashion based on workload
      const assignments = unassignedCampaigns.map((campaign, index) => {
        const csrIndex = index % sortedCSRs.length;
        return {
          campaignId: campaign.id,
          csrId: sortedCSRs[csrIndex].csr_id
        };
      });

      // Execute assignments
      await Promise.all(
        assignments.map(async ({ campaignId, csrId }) => {
          const { error } = await supabase
            .from('campaigns')
            .update({ assigned_csr: csrId })
            .eq('id', campaignId);
          
          if (error) throw error;
        })
      );

      refetch();
      refreshWorkloads(); // Refresh workload data after assignment
      setError('');
    } catch (err) {
      setError('Failed to auto-assign campaigns');
      console.error('Error in auto-assignment:', err);
    }
  };

  // Apply all filters to campaigns using useMemo for performance
  const filteredCampaigns = useMemo(() => {
    let result = campaigns;
    
    // Apply search filter
    if (filters.search) {
      result = searchCampaigns(result, filters.search);
    }
    
    // Apply status filter
    if (filters.status !== 'all') {
      result = filterCampaignsByStatus(result, filters.status);
    }
    
    // Apply assignment filter (keeping legacy filter for compatibility)
    const assignmentFilter = filters.assignment !== 'all' ? filters.assignment : filter;
    result = filterCampaignsByAssignment(result, assignmentFilter);
    
    // Apply date range filter
    if (filters.dateRange.from || filters.dateRange.to) {
      result = result.filter(campaign => {
        const campaignDate = new Date(campaign.created_at);
        const fromDate = filters.dateRange.from ? new Date(filters.dateRange.from) : null;
        const toDate = filters.dateRange.to ? new Date(filters.dateRange.to) : null;
        
        if (fromDate && campaignDate < fromDate) return false;
        if (toDate && campaignDate > toDate) return false;
        return true;
      });
    }
    
    return result;
  }, [campaigns, filters, filter]);
  
  // Sort filtered campaigns based on current sort configuration
  const sortedAndFilteredCampaigns = useMemo(() => {
    if (!campaignSortConfig.key) return filteredCampaigns;

    return [...filteredCampaigns].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;
      
      switch (campaignSortConfig.key) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'user_email':
          aValue = a.user_email || '';
          bValue = b.user_email || '';
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'assigned_csr_email':
          aValue = a.assigned_csr_email || '';
          bValue = b.assigned_csr_email || '';
          break;
        case 'company_name':
          aValue = a.company_name || '';
          bValue = b.company_name || '';
          break;
        default:
          return 0;
      }
      
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return campaignSortConfig.direction === 'asc' ? 1 : -1;
      if (bValue == null) return campaignSortConfig.direction === 'asc' ? -1 : 1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const result = aValue.localeCompare(bValue);
        return campaignSortConfig.direction === 'asc' ? result : -result;
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        const result = aValue - bValue;
        return campaignSortConfig.direction === 'asc' ? result : -result;
      }
      
      return 0;
    });
  }, [filteredCampaigns, campaignSortConfig]);
  
  // Handle campaign table sorting
  const handleCampaignSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (campaignSortConfig.key === key && campaignSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setCampaignSortConfig({ key, direction });
  };

  // Get sort indicator icon for campaigns
  const getCampaignSortIcon = (columnKey: string) => {
    if (campaignSortConfig.key !== columnKey) {
      return (
        <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    return campaignSortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 ml-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 ml-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Calculate campaign counts for filter display
  const campaignCounts = useMemo(() => ({
    total: campaigns.length,
    assigned: campaigns.filter(c => c.assigned_csr).length,
    unassigned: campaigns.filter(c => !c.assigned_csr).length
  }), [campaigns]);

  // Handle campaign selection for bulk operations
  const toggleCampaignSelection = (campaignId: string) => {
    setSelectedCampaigns(prev => 
      prev.includes(campaignId) 
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  const selectAllVisible = () => {
    setSelectedCampaigns(sortedAndFilteredCampaigns.map(c => c.id));
  };

  const clearSelection = () => {
    setSelectedCampaigns([]);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Enhanced CSR Assignment</h2>
          <p className="text-gray-600">Manage campaign assignments with workload balancing</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowThresholdSettings(!showThresholdSettings)}
            className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
            title="Configure workload threshold"
          >
            ⚙️ Threshold: {workloadThreshold}
          </button>
          <button
            onClick={handleAutoAssign}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Auto-Assign
          </button>
          <button
            onClick={() => setShowBulkActions(!showBulkActions)}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
          >
            Bulk Actions
          </button>
        </div>
      </div>

      {/* Workload Threshold Settings Modal */}
      {showThresholdSettings && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-blue-900">Workload Threshold Settings</h3>
            <button
              onClick={() => setShowThresholdSettings(false)}
              className="text-blue-500 hover:text-blue-700"
            >
              ✕
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-blue-800 mb-2">
                Maximum Workload Threshold
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="number"
                  min="10"
                  max="500"
                  value={workloadThreshold}
                  onChange={(e) => setWorkloadThreshold(Number(e.target.value))}
                  className="px-3 py-2 border border-blue-300 rounded-md w-24 text-center"
                />
                <span className="text-sm text-blue-700">
                  Points (Current: {workloadThreshold})
                </span>
              </div>
            </div>
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Workload Calculation:</p>
              <ul className="space-y-1">
                <li>• Company assignments: 5 points each</li>
                <li>• Individual campaigns: 3 points each</li>
                <li>• Lead responses: 1 point each</li>
              </ul>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Existing assignments above the new threshold will continue to work. 
                The threshold only applies to new bulk assignments.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CSR Workload Dashboard */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">CSR Workload Overview</h3>
            <p className="text-sm text-gray-600">
              {workloadStats.totalCSRs} CSRs • Average workload: {workloadStats.averageWorkload} • Balance score: {workloadStats.balanceScore}%
            </p>
            <p className="text-xs text-gray-500">
              Last updated: {lastRefresh.toLocaleTimeString()}
              {autoRefresh && ' • Auto-refresh: ON'}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                autoRefresh 
                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              title={autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh'}
            >
              {autoRefresh ? '🟢 Auto' : '⏸️ Manual'}
            </button>
            <button
              onClick={() => {
                refreshWorkloads();
                setLastRefresh(new Date());
              }}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              title="Refresh workload data now"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {csrWorkloads.map((workload) => (
            <CSRWorkloadCard
              key={workload.csr_id}
              workload={workload}
              onClick={() => setSelectedCSR(selectedCSR === workload.csr_id ? null : workload.csr_id)}
              isSelected={selectedCSR === workload.csr_id}
            />
          ))}
        </div>
        
        {csrWorkloads.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">👥</div>
            <p>No CSRs found. Create CSR users to begin assigning campaigns.</p>
          </div>
        )}
      </div>

      {/* Enhanced Bulk Assignment Panel */}
      <BulkAssignmentPanel
        selectedCampaigns={selectedCampaigns}
        csrWorkloads={csrWorkloads}
        onBulkAssign={async (csrId) => {
          setBulkAssignCSR(csrId || '');
          await handleBulkAssign();
        }}
        onSelectAll={selectAllVisible}
        onClearSelection={clearSelection}
        onClose={() => setShowBulkActions(false)}
        isVisible={showBulkActions}
        isProcessing={bulkProcessing}
        workloadThreshold={workloadThreshold}
      />

      {/* Enhanced Campaign Filters */}
      <div className="mb-6">
        <CampaignFilters
          onFiltersChange={setFilters}
          campaignCounts={campaignCounts}
          className="mb-4"
        />
        
        {/* Quick Filter Buttons (Legacy Support) */}
        <div className="flex items-center justify-between">
          <div className="flex space-x-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              All ({campaigns.length})
            </button>
            <button
              onClick={() => setFilter('assigned')}
              className={`px-4 py-2 rounded ${filter === 'assigned' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              Assigned ({campaigns.filter(c => c.assigned_csr).length})
            </button>
            <button
              onClick={() => setFilter('unassigned')}
              className={`px-4 py-2 rounded ${filter === 'unassigned' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              Unassigned ({campaigns.filter(c => !c.assigned_csr).length})
            </button>
          </div>
          <div className="text-sm text-gray-600">
            Showing {filteredCampaigns.length} of {campaigns.length} campaigns
          </div>
        </div>
      </div>

      {/* Preview Mode Banner */}
      {csrUsers.some(csr => csr.id.includes('preview')) && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center">
            <div className="text-yellow-800">
              <strong>Preview Mode:</strong> Showing sample CSR users. Deploy the database functions to see real CSR users.
            </div>
          </div>
        </div>
      )}

      {displayError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{displayError}</p>
        </div>
      )}

      {/* Campaign Assignment Table - Desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {showBulkActions && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Select
                </th>
              )}
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleCampaignSort('name')}
              >
                <div className="flex items-center">
                  Campaign
                  {getCampaignSortIcon('name')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleCampaignSort('user_email')}
              >
                <div className="flex items-center">
                  Owner
                  {getCampaignSortIcon('user_email')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleCampaignSort('company_name')}
              >
                <div className="flex items-center">
                  Company
                  {getCampaignSortIcon('company_name')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleCampaignSort('status')}
              >
                <div className="flex items-center">
                  Status
                  {getCampaignSortIcon('status')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleCampaignSort('created_at')}
              >
                <div className="flex items-center">
                  Created
                  {getCampaignSortIcon('created_at')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleCampaignSort('assigned_csr_email')}
              >
                <div className="flex items-center">
                  Assigned CSR
                  {getCampaignSortIcon('assigned_csr_email')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedAndFilteredCampaigns.length === 0 ? (
              <tr>
                <td colSpan={showBulkActions ? 8 : 7} className="px-6 py-4 text-center text-gray-500">
                  No campaigns found for the selected filter
                </td>
              </tr>
            ) : (
              sortedAndFilteredCampaigns.map((campaign) => {
                // Determine assignment type for this campaign
                const userCompanyId = campaign.company_id || campaigns.find(c => c.user_id === campaign.user_id)?.company_id;
                const companyCSRId = userCompanyId ? companiesData[userCompanyId] : undefined;
                const assignmentType = getAssignmentType(campaign, companyCSRId);
                
                return (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  {showBulkActions && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedCampaigns.includes(campaign.id)}
                        onChange={() => toggleCampaignSelection(campaign.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{campaign.user_email || 'Unknown'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{campaign.company_name || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <EnhancedStatusBadge 
                      campaign={campaign} 
                      showPriority={true}
                      size="medium"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {new Date(campaign.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {campaign.assigned_csr_email ? (
                      <div className="space-y-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getAssignmentTypeStyles(assignmentType)}`}>
                          {getAssignmentTypeLabel(assignmentType)}
                        </span>
                        <div className="text-xs text-gray-600">
                          {campaign.assigned_csr_email}
                        </div>
                      </div>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Unassigned
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <select
                        value={campaign.assigned_csr || ''}
                        onChange={(e) => handleAssignCSR(campaign.id, e.target.value || null)}
                        className="text-sm border border-gray-300 rounded px-2 py-1 w-full"
                        disabled={assignmentType === 'company'}
                      >
                        <option value="">Unassigned</option>
                        {csrUsers.map((csr) => (
                          <option key={csr.id} value={csr.id}>
                            {csr.email}
                          </option>
                        ))}
                      </select>
                      {assignmentType === 'company' && (
                        <div className="text-xs text-blue-600">
                          Managed by company CSR
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Campaign Assignment Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {filteredCampaigns.length === 0 ? (
          <div className="bg-white p-6 rounded-lg shadow text-center text-gray-500">
            <div className="text-4xl mb-2">📋</div>
            <p>No campaigns found for the selected filter</p>
          </div>
        ) : (
          sortedAndFilteredCampaigns.map((campaign) => {
            // Determine assignment type for this campaign
            const userCompanyId = campaign.company_id || campaigns.find(c => c.user_id === campaign.user_id)?.company_id;
            const companyCSRId = userCompanyId ? companiesData[userCompanyId] : undefined;
            const assignmentType = getAssignmentType(campaign, companyCSRId);
            
            return (
              <div key={campaign.id} className="bg-white p-4 rounded-lg shadow border border-gray-200">
                {/* Mobile Card Header */}
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {campaign.name}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {campaign.user_email || 'Unknown'}
                    </p>
                    {campaign.company_name && (
                      <p className="text-xs text-gray-500">
                        {campaign.company_name}
                      </p>
                    )}
                  </div>
                  {showBulkActions && (
                    <input
                      type="checkbox"
                      checked={selectedCampaigns.includes(campaign.id)}
                      onChange={() => toggleCampaignSelection(campaign.id)}
                      className="ml-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  )}
                </div>
                
                {/* Status and Assignment Type */}
                <div className="flex items-center gap-2 mb-3">
                  <EnhancedStatusBadge 
                    campaign={campaign} 
                    showPriority={true}
                    size="small"
                  />
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getAssignmentTypeStyles(assignmentType)}`}>
                    {getAssignmentTypeLabel(assignmentType)}
                  </span>
                </div>
                
                {/* Campaign Date */}
                <div className="text-xs text-gray-500 mb-3">
                  Created: {new Date(campaign.created_at).toLocaleDateString()}
                </div>
                
                {/* CSR Assignment Section */}
                <div className="border-t border-gray-100 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-700">Assigned CSR</span>
                  </div>
                  
                  {campaign.assigned_csr_email ? (
                    <div className="mb-2">
                      <div className="text-sm text-gray-900">
                        {campaign.assigned_csr_email}
                      </div>
                      {assignmentType === 'company' && (
                        <div className="text-xs text-blue-600 mt-1">
                          🏢 Managed by company CSR
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 mb-2">
                      Unassigned
                    </div>
                  )}
                  
                  {/* Assignment Control */}
                  <select
                    value={campaign.assigned_csr || ''}
                    onChange={(e) => handleAssignCSR(campaign.id, e.target.value || null)}
                    className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                    disabled={assignmentType === 'company'}
                  >
                    <option value="">Unassigned</option>
                    {csrUsers.map((csr) => (
                      <option key={csr.id} value={csr.id}>
                        {csr.email}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default EnhancedCSRAssignment;